import {
  CheckCircle as CheckCircleIcon,
  Home as HomeIcon,
  InfoOutlined,
  LocationOn as LocationOnIcon,
  PersonOutline,
  ReceiptLong,
  ShoppingBagOutlined as ShoppingBagOutlinedIcon,
} from "@mui/icons-material";
import {
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  IconButton,
  Paper,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useTranslation } from "react-i18next";
import NoTranslate, {
  noTranslateProps,
} from "../../components/common/NoTranslate";
import Price from "../../components/common/Price";
import { amazonColors } from "../../config/amazonTheme";
import { getMyProfile } from "../../services/profileService";
import { useAuthStore } from "../../store/useAuthStore";

// --- Types ---
interface OrderConfirmationState {
  orderNumber?: string;
  paymentIntentId?: string;
  amount?: number;
  currency?: string;
  pickupLocation?: string;
  customerName?: string;
  shippingDue?: number; // Estimated delivery/cargo fee due at pickup
  itemsTotal?: number; // Total price of items before tax/fees
}

// --- Helpers ---
const normalizeName = (...parts: Array<string | null | undefined>): string => {
  return parts
    .filter((part) => part && part !== "undefined" && part !== "null") // Filter out bad values
    .map((part) => part?.trim()) // Trim whitespace
    .filter(Boolean) // Remove empty strings
    .join(" ");
};

const OrderConfirmation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { t } = useTranslation();

  const fallbackContactLabel = t("orderConfirmation.fallbackContact");
  const fallbackPickupLocation = t("orderConfirmation.fallbackPickupLocation");

  // Cast state safely
  const state = location.state as OrderConfirmationState | null;
  const orderNumber = state?.orderNumber;
  const shippingDue = state?.shippingDue;
  const itemsTotalForSavings =
    typeof state?.itemsTotal === "number" ? state.itemsTotal : state?.amount;
  const taxSavingsRate = 0.0887;
  const hasTaxSavings = typeof itemsTotalForSavings === "number";
  const estimatedTaxSavings = hasTaxSavings
    ? itemsTotalForSavings * taxSavingsRate
    : 0;
  const typicalStoreTotal = hasTaxSavings
    ? itemsTotalForSavings + estimatedTaxSavings
    : 0;
  const taxRatePercent = 8.87;

  // --- Logic: Name Resolution ---
  // Priority: 1. Name passed from checkout -> 2. Name from Auth Store -> 3. Fallback
  const nameFromState = normalizeName(state?.customerName);
  const nameFromStore = normalizeName(user?.firstName, user?.lastName);

  const finalDisplayName =
    nameFromState || nameFromStore || fallbackContactLabel;
  const [resolvedName, setResolvedName] = useState(finalDisplayName);
  const displayLocation = state?.pickupLocation || fallbackPickupLocation;

  // Keep local display name in sync with inputs
  useEffect(() => {
    setResolvedName(finalDisplayName);
  }, [finalDisplayName]);

  // If we still don't have a name, try to fetch it from the profile
  useEffect(() => {
    if (nameFromState || nameFromStore) return;

    let isCancelled = false;
    const fetchProfileName = async () => {
      try {
        const resp = await getMyProfile();
        const profileName = normalizeName(
          resp.data?.firstName,
          resp.data?.lastName
        );
        if (!isCancelled && profileName) {
          setResolvedName(profileName);
        }
      } catch (_e) {
        // Keep fallback label if profile name cannot be fetched
      }
    };

    fetchProfileName();
    return () => {
      isCancelled = true;
    };
  }, [nameFromState, nameFromStore]);

  // --- Handlers ---
  const goToOrders = () => {
    navigate("/profile", { state: { initialSection: "orders" } });
  };

  const [shippingInfoAnchorEl, setShippingInfoAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const shippingInfoOpen = Boolean(shippingInfoAnchorEl);
  const [taxSavingsAnchorEl, setTaxSavingsAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const taxSavingsOpen = Boolean(taxSavingsAnchorEl);

  const handleShippingInfoClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setShippingInfoAnchorEl(event.currentTarget);
  };

  const handleShippingInfoClose = () => {
    setShippingInfoAnchorEl(null);
  };

  const handleTaxSavingsInfoClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setTaxSavingsAnchorEl(event.currentTarget);
  };

  const handleTaxSavingsInfoClose = () => {
    setTaxSavingsAnchorEl(null);
  };

  const detailRowSx = {
    px: 1.5,
    py: 1.25,
    borderRadius: 2,
    border: `1px solid ${alpha(amazonColors.blue, 0.12)}`,
    backgroundColor: alpha(amazonColors.blue, 0.04),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1.5,
  };

  // --- Render: Invalid Order State ---
  if (!orderNumber) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }} {...noTranslateProps}>
        <Paper
          {...noTranslateProps}
          sx={{
            p: { xs: 4, md: 5 },
            textAlign: "center",
            borderRadius: 4,
            boxShadow: 4,
            background:
              "linear-gradient(135deg, rgba(239,68,68,0.08), rgba(248,113,113,0.12))",
          }}
        >
          <Avatar
            sx={{
              bgcolor: "error.main",
              width: 72,
              height: 72,
              mx: "auto",
              mb: 2,
              fontSize: 28,
            }}
          >
            !
          </Avatar>
          <Typography variant="h4" gutterBottom color="error.main">
            {t("orderConfirmation.invalidOrder")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t("orderConfirmation.orderNotFound")}
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<HomeIcon />}
            onClick={() => navigate("/")}
            sx={{ minWidth: 160 }}
          >
            {t("orderConfirmation.goHome")}
          </Button>
        </Paper>
      </Container>
    );
  }

  // --- Render: Success State ---
  return (
    <Box
      {...noTranslateProps}
      sx={{
        backgroundColor: "#f6f7fb",
        minHeight: "100vh",
        py: { xs: 4, md: 6 },
        backgroundImage: `radial-gradient(circle at 12% 8%, ${alpha(
          amazonColors.orange,
          0.16
        )} 0%, transparent 45%), radial-gradient(circle at 90% 2%, ${alpha(
          amazonColors.blue,
          0.14
        )} 0%, transparent 40%)`,
      }}
    >
      <Container maxWidth="md">
        <Paper
          {...noTranslateProps}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            boxShadow: "0 24px 60px rgba(15, 23, 42, 0.12)",
            border: "1px solid",
            borderColor: alpha(amazonColors.blue, 0.12),
            background: "linear-gradient(180deg, #ffffff 0%, #fffaf5 100%)",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              width: 170,
              height: 170,
              top: -90,
              right: -70,
              borderRadius: "50%",
              backgroundColor: alpha(amazonColors.orange, 0.14),
            },
            "&::after": {
              content: '""',
              position: "absolute",
              width: 140,
              height: 140,
              bottom: -70,
              left: -60,
              borderRadius: "50%",
              backgroundColor: alpha(amazonColors.blue, 0.12),
            },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 6,
              background: `linear-gradient(90deg, ${amazonColors.orange} 0%, ${amazonColors.yellow} 100%)`,
            }}
          />
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Stack spacing={{ xs: 2.5, md: 3 }}>
              {/* Success Header */}
              <Stack spacing={1.5} alignItems="center" textAlign="center">
                <Chip
                  icon={<CheckCircleIcon />}
                  label={t("orderConfirmation.orderConfirmed")}
                  color="success"
                  variant="outlined"
                  sx={{
                    fontWeight: 700,
                    color: amazonColors.green,
                    borderColor: alpha(amazonColors.green, 0.4),
                    bgcolor: alpha(amazonColors.green, 0.08),
                  }}
                />
                <Typography variant="h4" fontWeight={800}>
                  {t("orderConfirmation.thankYou")}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {t("orderConfirmation.orderPlacedDescription")}
                </Typography>
              </Stack>

              <Stack spacing={2}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    borderColor: alpha(amazonColors.blue, 0.15),
                    backgroundColor: "#ffffff",
                    boxShadow: "0px 14px 30px rgba(0,0,0,0.06)",
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack spacing={1.5}>
                      <Box sx={detailRowSx}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1.5}
                        >
                          <Avatar
                            sx={{
                              width: 34,
                              height: 34,
                              bgcolor: alpha(amazonColors.blue, 0.12),
                              color: amazonColors.blue,
                            }}
                          >
                            <ReceiptLong fontSize="small" />
                          </Avatar>
                          <Typography variant="body2" color="text.secondary">
                            {t("orderConfirmation.orderNumber")}
                          </Typography>
                        </Stack>
                        <Typography variant="subtitle2" fontWeight={800}>
                          <NoTranslate>{orderNumber}</NoTranslate>
                        </Typography>
                      </Box>

                      <Box sx={detailRowSx}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1.5}
                        >
                          <Avatar
                            sx={{
                              width: 34,
                              height: 34,
                              bgcolor: alpha(amazonColors.orange, 0.12),
                              color: amazonColors.orange,
                            }}
                          >
                            <PersonOutline fontSize="small" />
                          </Avatar>
                          <Typography variant="body2" color="text.secondary">
                            {t("orderConfirmation.pickupPerson")}
                          </Typography>
                        </Stack>
                        <Typography variant="subtitle2" fontWeight={700}>
                          <NoTranslate>{resolvedName}</NoTranslate>
                        </Typography>
                      </Box>

                      <Box sx={detailRowSx}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1.5}
                        >
                          <Avatar
                            sx={{
                              width: 34,
                              height: 34,
                              bgcolor: alpha(amazonColors.red, 0.12),
                              color: amazonColors.red,
                            }}
                          >
                            <LocationOnIcon fontSize="small" />
                          </Avatar>
                          <Typography variant="body2" color="text.secondary">
                            {t("orderConfirmation.pickupLocation")}
                          </Typography>
                        </Stack>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {displayLocation}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 1.2,
                    borderRadius: 2,
                    bgcolor: alpha(amazonColors.blue, 0.08),
                    color: amazonColors.blue,
                  }}
                >
                  <InfoOutlined sx={{ fontSize: 18 }} />
                  <Typography variant="body2" color="text.secondary">
                    {t("orderConfirmation.orderReadyNotification")}
                  </Typography>
                </Box>

                <Stack spacing={1.5}>
                  <Card
                    variant="outlined"
                    sx={{
                      backgroundColor: "#FFF4E6",
                      borderRadius: 3,
                      borderColor: "#FFD8A8",
                      boxShadow: "0px 12px 26px rgba(0,0,0,0.05)",
                    }}
                  >
                    <CardContent sx={{ p: 2.25 }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ gap: 2, flexWrap: "wrap" }}
                      >
                        <Box sx={{ minWidth: 180, flex: 1 }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 800 }}
                            >
                              {t("orderConfirmation.shippingDueAtPickup")}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={handleShippingInfoClick}
                              sx={{ p: 0.25 }}
                            >
                              <InfoOutlined
                                sx={{
                                  fontSize: 16,
                                  color: amazonColors.orange,
                                }}
                              />
                            </IconButton>
                          </Stack>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontSize: "11px",
                              display: "block",
                            }}
                          >
                            {t("orderConfirmation.shippingDueExplanation")}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            px: 1.6,
                            py: 0.6,
                            borderRadius: 999,
                            bgcolor: "#ffffff",
                            border: "1px solid #F2C77E",
                          }}
                        >
                          <Typography
                            translate="no"
                            variant="body2"
                            sx={{ fontWeight: 800 }}
                          >
                            {shippingDue !== undefined ? (
                              <>
                                ~
                                <Price
                                  value={shippingDue}
                                  disableCentsHeuristic
                                />
                              </>
                            ) : (
                              t("orderConfirmation.atPickup")
                            )}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>

                  {hasTaxSavings && (
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        borderStyle: "dashed",
                        borderColor: alpha("#4CAF50", 0.4),
                        backgroundColor: alpha("#4CAF50", 0.06),
                        boxShadow: "0px 12px 26px rgba(0,0,0,0.05)",
                      }}
                    >
                      <CardContent sx={{ p: 2.25 }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ gap: 2, flexWrap: "wrap" }}
                        >
                          <Box sx={{ minWidth: 180, flex: 1 }}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={0.5}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 800,
                                  color: "#1B5E20",
                                  fontSize: "0.95rem",
                                }}
                              >
                                {t("orderConfirmation.taxSavings")}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={handleTaxSavingsInfoClick}
                                sx={{ p: 0.25 }}
                              >
                                <InfoOutlined
                                  sx={{
                                    fontSize: 16,
                                    color: amazonColors.orange,
                                  }}
                                />
                              </IconButton>
                            </Stack>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                display: "block",
                                mt: 0.5,
                              }}
                            >
                              {t("orderConfirmation.noSalesTax")}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              px: 1.6,
                              py: 0.6,
                              borderRadius: 999,
                              bgcolor: "#ffffff",
                              border: `1px solid ${alpha("#4CAF50", 0.3)}`,
                            }}
                          >
                            <Typography
                              translate="no"
                              variant="body2"
                              sx={{
                                fontWeight: 800,
                                color: "#1B5E20",
                                fontSize: "0.95rem",
                              }}
                            >
                              <Price
                                value={estimatedTaxSavings}
                                disableCentsHeuristic
                                format="locale"
                              />
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  )}
                </Stack>
              </Stack>

              <Popover
                open={shippingInfoOpen}
                anchorEl={shippingInfoAnchorEl}
                onClose={handleShippingInfoClose}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                PaperProps={noTranslateProps}
                sx={{
                  "& .MuiPopover-paper": {
                    borderRadius: 2,
                    minWidth: 200,
                    maxWidth: 280,
                    p: 2,
                  },
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 1, color: amazonColors.orange }}
                >
                  {t("orderConfirmation.aboutEstimatedShipping")}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  <strong>{t("orderConfirmation.shippingRoute")}</strong>
                  <br />
                  {t("orderConfirmation.shippingEstimateDetails")}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontStyle: "italic", display: "block", mt: 1 }}
                >
                  {t("orderConfirmation.shippingEstimateDisclaimer")}
                </Typography>
              </Popover>

              {hasTaxSavings && (
                <Popover
                  open={taxSavingsOpen}
                  anchorEl={taxSavingsAnchorEl}
                  onClose={handleTaxSavingsInfoClose}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  PaperProps={{
                    elevation: 4,
                    sx: {
                      borderRadius: 2,
                      width: 240,
                      overflow: "hidden",
                    },
                    ...noTranslateProps,
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      bgcolor: "#fff",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{ fontSize: "0.9rem" }}
                    >
                      {t("orderConfirmation.taxSavingsBreakdown")}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {t("orderConfirmation.itemValue")}
                      </Typography>
                      <Typography
                        translate="no"
                        variant="caption"
                        fontWeight={600}
                        sx={{ fontSize: "0.85rem" }}
                      >
                        <Price
                          value={itemsTotalForSavings}
                          disableCentsHeuristic
                          format="locale"
                        />
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1.5,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {t("orderConfirmation.estimatedUsTax", {
                          rate: taxRatePercent,
                        })}
                      </Typography>
                      <Typography
                        translate="no"
                        variant="caption"
                        color="error.main"
                        sx={{ textDecoration: "line-through", opacity: 0.7 }}
                      >
                        <Price
                          value={estimatedTaxSavings}
                          disableCentsHeuristic
                          format="locale"
                        />
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        background:
                          "linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 100%)",
                        border: "1px solid #C8E6C9",
                        borderRadius: 1.5,
                        py: 1,
                        px: 1.5,
                        mb: 1.5,
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          textTransform: "uppercase",
                          fontWeight: 800,
                          letterSpacing: 0.5,
                          color: "#2E7D32",
                          fontSize: "0.65rem",
                          display: "block",
                          mb: 0.25,
                        }}
                      >
                        {t("orderConfirmation.youSave")}
                      </Typography>
                      <Typography
                        translate="no"
                        variant="h6"
                        fontWeight={800}
                        sx={{
                          color: "#1B5E20",
                          fontSize: "1.1rem",
                          lineHeight: 1,
                        }}
                      >
                        <Price
                          value={estimatedTaxSavings}
                          disableCentsHeuristic
                          format="locale"
                        />
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 1.5, borderStyle: "dashed" }} />
                    <Box sx={{ display: "grid", gap: 0.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={500}
                        >
                          {t("orderConfirmation.typicalRetailer")}
                        </Typography>
                        <Typography
                          translate="no"
                          variant="caption"
                          sx={{
                            textDecoration: "line-through",
                            color: "text.secondary",
                          }}
                        >
                          <Price
                            value={typicalStoreTotal}
                            disableCentsHeuristic
                            format="locale"
                          />
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color="primary.main"
                        >
                          {t("orderConfirmation.withUs2tjk")}
                        </Typography>
                        <Typography
                          translate="no"
                          variant="subtitle2"
                          fontWeight={800}
                          color="text.primary"
                          sx={{ fontSize: "0.95rem" }}
                        >
                          <Price
                            value={itemsTotalForSavings}
                            disableCentsHeuristic
                            format="locale"
                          />
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Popover>
              )}

              {/* Actions */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{ pt: 1 }}
              >
                <Button
                  variant="contained"
                  fullWidth
                  onClick={goToOrders}
                  sx={{
                    backgroundColor: amazonColors.orange,
                    color: "white",
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    py: 1.1,
                    px: 2,
                    borderRadius: 2,
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: amazonColors.orangeHover,
                      boxShadow: "none",
                    },
                  }}
                >
                  {t("orderConfirmation.viewMyOrders")}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ShoppingBagOutlinedIcon />}
                  fullWidth
                  onClick={() => navigate("/")}
                  sx={{
                    color: amazonColors.orange,
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    py: 1.1,
                    px: 2,
                    borderRadius: 2,
                    borderColor: amazonColors.orange,
                    "&:hover": {
                      backgroundColor: amazonColors.gray[50],
                      borderColor: amazonColors.orangeHover,
                      color: amazonColors.orangeHover,
                    },
                  }}
                >
                  {t("orderConfirmation.continueShopping")}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default OrderConfirmation;
